import { LIGHTBOX_STYLES } from '../../lib/lightbox';
import { __, isPreviewableMedia } from '../../utils';
import type { FeedMedia } from '../FeedItem/FeedItem';
import { Box, ButtonBase, Image } from '@presov.sk/presov-ui-react';
import React from 'react';
import Lightbox from 'yet-another-react-lightbox';
import VideoPlugin from 'yet-another-react-lightbox/plugins/video';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';

/** Jediná fotka v paneli na desktope. V mobilnom sheete je nižšia - `compact`. */
const SINGLE_HEIGHT = 186;
const SINGLE_HEIGHT_COMPACT = 132;
/**
 * Viac fotiek stojí v troch slotoch na výšku (4:5). Viac než tri sa do 412 px
 * panela nezmestia čitateľne - tretí slot potom nesie `+N` a otvára galériu.
 */
const SLOTS = 3;

type Props = {
  media?: FeedMedia[];
  /** Alternatívny text - názov podnetu, nie názov súboru. */
  title: string;
  /** Mobilný sheet: nižšia jediná fotka. */
  compact?: boolean;
};

const mediaAlt = (title: string, index: number) =>
  index === 0 ? title : `${title} - ${__('fotka')} ${index + 1}`;

/**
 * Jeden slot: obrázok, alebo prvý snímok videa. Video sa neprehráva - je to
 * náhľad, ktorý po kliknutí otvorí galériu.
 *
 * Obsah je **celý** (`contain`), nie orezaný: fotka podnetu je dôkaz a orezaná
 * mohla schovať práve to podstatné. Prázdne pruhy vedľa nej vypĺňa tá istá
 * fotka rozmazaná (CSS pozadie, nie druhý `<Image>` - pri mŕtvej adrese by sa
 * náhradný obrázok ukázal dvakrát). Obsah je absolútne cez celý slot: v slote
 * s pomerom strán sa `height: 100%` neodvodí a fotka na výšku by pretiekla.
 */
const Slot: React.FC<{ item: FeedMedia; alt: string }> = ({ item, alt }) => {
  const fill = {
    position: 'absolute',
    inset: 0,
    width: 1,
    height: 1,
    objectFit: 'contain',
    display: 'block',
  } as const;

  return item.type.startsWith('video/') ? (
    <Box
      component='video'
      // Bez posunu o desatinu sekundy ukážu niektoré prehliadače čierny snímok.
      src={`${item.url}#t=0.1`}
      muted
      playsInline
      preload='metadata'
      aria-label={alt}
      sx={{ ...fill, backgroundColor: 'common.black' }}
    />
  ) : (
    <>
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `url(${JSON.stringify(item.url)})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          filter: 'blur(12px) brightness(.85)',
          // Rozmazanie pri okraji presvitá do priehľadna, zväčšenie ten svetlý
          // lem schová za `overflow: hidden` slotu.
          transform: 'scale(1.2)',
        }}
      />
      <Image src={item.url} alt={alt} sx={fill} />
    </>
  );
};

/**
 * Fotky podnetu v detaile.
 *
 * **Jedna fotka** stojí sama cez celú šírku (186 px, v mobilnom sheete 132 px).
 * **Viac fotiek** stojí v troch rovnakých slotoch 4:5 - kým tu bola prvá veľká
 * a pás náhľadov pod ňou, jediný náhľad sa roztiahol cez celú šírku panela na
 * 58 px výšky a vyzeral ako pruh, nie ako fotka. Pri viac ako troch nesie
 * posledný slot `+N` a otvára galériu, inak by sa zvyšné fotky stratili bez
 * stopy.
 *
 * Prílohy dostáva **z položky feedu**, nie z vlastného dotazu: `getFeed` ich
 * nesie celé práve preto, aby detail nemusel ťahať podnet druhýkrát. Do slotov
 * a do galérie ide len to, čo prehliadač vie ukázať (`isPreviewableMedia`);
 * PDF a dokumenty, ktoré občan priložil, sú pod popisom ([[ReportDocuments]]).
 */
const ReportPhotos: React.FC<Props> = ({ media, title, compact = false }) => {
  const photos = React.useMemo(
    () => (media ?? []).filter((item) => isPreviewableMedia(item.type)),
    [media],
  );
  const [openAt, setOpenAt] = React.useState<number>();

  if (!photos.length) return null;

  const single = photos.length === 1;
  const shown = photos.slice(0, SLOTS);
  // Koľko fotiek sloty neukážu. Číslo sadne na posledný slot ako `+N`.
  const hidden = photos.length - shown.length;

  return (
    <>
      <Box
        sx={{
          flex: 'none',
          display: 'grid',
          gap: 0.75,
          gridTemplateColumns: single ? '1fr' : `repeat(${SLOTS}, 1fr)`,
        }}
      >
        {shown.map((item, index) => {
          const more = index === shown.length - 1 && hidden > 0;

          return (
            <ButtonBase
              key={item.url}
              onClick={() => setOpenAt(index)}
              aria-label={
                more || single ? __('Otvoriť galériu') : mediaAlt(title, index)
              }
              sx={(theme) => ({
                position: 'relative',
                display: 'block',
                minWidth: 0,
                overflow: 'hidden',
                borderRadius: `${theme.typography.pxToRem(single ? 14 : 9)}`,
                backgroundColor: theme.palette.neutralSurface,
                ...(single
                  ? { height: compact ? SINGLE_HEIGHT_COMPACT : SINGLE_HEIGHT }
                  : { aspectRatio: '4 / 5' }),
              })}
            >
              <Slot item={item} alt={mediaAlt(title, index)} />
              {more ? (
                <Box
                  aria-hidden
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'grid',
                    placeItems: 'center',
                    backgroundColor: 'rgba(23, 21, 18, 0.55)',
                    color: '#FFFFFF',
                    fontSize: (theme) => theme.typography.pxToRem(14),
                    fontWeight: 700,
                  }}
                >
                  {`+${hidden + 1}`}
                </Box>
              ) : null}
            </ButtonBase>
          );
        })}
      </Box>

      <Lightbox
        index={openAt ?? 0}
        open={openAt !== undefined}
        close={() => setOpenAt(undefined)}
        slides={photos.map((item) =>
          item.type.startsWith('image/')
            ? { src: item.url }
            : {
                type: 'video' as const,
                sources: [{ src: item.url, type: item.type }],
              },
        )}
        plugins={[Zoom, VideoPlugin]}
        styles={LIGHTBOX_STYLES}
      />
    </>
  );
};

export default ReportPhotos;
